import { Center, Box, SpinnerProps, Image, VisuallyHidden } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import React, { useEffect } from 'react';
import { useTranslation } from 'utils/translation';

export type LoaderProps = React.PropsWithChildren<{
  height?: string | number;
  width?: string | number;
  label?: string;
  debugName?: string;
  isLoaded?: boolean;
}>;

const colorPulse = keyframes`
  0%, 100% { 
    fill: #614979;
    opacity: 1;
  }
  50% { 
    fill: #B79AD2;
    opacity: 0.45;
  }
`;

export function CelsiaSpinner({ label }: SpinnerProps) {
  return (
    <Box position="relative" w="100%" h="100%">
      <Center position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)">
        <Box as="svg" width="100px" height="100px" viewBox="0 0 100 100">
          <Box as="g" sx={{ transform: 'translate(30px, 25px)' }}>
            <Box
              as="path"
              d="M0 0V49.6785L41.348 24.8236L0 0ZM30.6571 26.7982L6.92649 41.0592L14.8166 26.7982H30.6571ZM30.6571 22.9117H14.8468L6.95664 8.65064L30.6571 22.9117ZM11.6245 24.8549L3.73429 39.1159V10.5939C3.70418 10.5939 11.6245 24.8549 11.6245 24.8549Z"
              fill="#614979"
              animation={`${colorPulse} 2.5s infinite`}
            />
          </Box>
        </Box>
      </Center>
      <VisuallyHidden>{label}</VisuallyHidden>
    </Box>
  );
}

export const Loader = React.forwardRef(function Loader(
  { height = '100%', width = '100%', label, debugName, children, isLoaded = false }: LoaderProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const { t, ready } = useTranslation(undefined, { useSuspense: false });
  useEffect(() => {
    if (import.meta.env?.NODE_ENV == 'development')
      if (debugName) {
        console.debug('Loader ', debugName, 'loaded');
        return () => console.debug('Loader', debugName, 'destroyed');
      }
  }, [debugName]);
  return isLoaded && ready ? (
    <>{children}</>
  ) : (
    <Center height={height} width={width} ref={ref} bg="transparent">
      {!!height && <CelsiaSpinner label={(label ?? ready) ? t('loading') : 'Loading...'} />}
    </Center>
  );
});
